import React, { useEffect, useState } from 'react';
import type { JobApplication, JobOfferLetter, JobPosting } from '../../../types/hiring';
import type { ContractorApplicant, ContractorAgreement } from '../../../types/contractorOnboard';
import type { CrewJob } from '../../../types/crewJobs';
import { formatOfferPay } from '../../../utils/jobOfferLetter';
import { formatContractorPay } from '../../../utils/contractorAgreement';
import { subscribeLatestContractorAgreement } from '../../../utils/contractorOnboardFirestore';
import type { ContractorOnboardViewerOpen } from '../../../utils/contractorOnboardViewer';
import JobApplicationDetail from './JobApplicationDetail';
import ContractorAgreementPane from './ContractorAgreementPane';

export type HiredOfferRow = {
  kind: 'offer';
  id: string;
  application: JobApplication;
  offer: JobOfferLetter;
};

export type HiredContractorRow = {
  kind: 'contractor';
  id: string;
  applicant: ContractorApplicant;
};

export type HiredRosterRow = HiredOfferRow | HiredContractorRow;

type Props = {
  seat: 'employee' | 'contractor';
  rows: HiredRosterRow[];
  postings: JobPosting[];
  crewJobs: CrewJob[];
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
  reviewerUid: string;
  onOpenPacketDoc?: (doc: ContractorOnboardViewerOpen) => void;
};

function personName(row: HiredRosterRow): string {
  if (row.kind === 'offer') return `${row.application.firstName} ${row.application.lastName}`.trim();
  return `${row.applicant.firstName} ${row.applicant.lastName}`.trim();
}

function roleLabel(row: HiredRosterRow): string {
  if (row.kind === 'offer') return row.offer.fields.positionTitle || row.application.jobTitle || '—';
  return row.applicant.lastJobTitle || '1099';
}

function payLabel(row: HiredRosterRow): string {
  if (row.kind === 'offer') return formatOfferPay(row.offer.fields);
  return '—';
}

function startLabel(iso: string): string {
  if (!iso) return '—';
  const day = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(day.getTime())) return '—';
  return day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function extraLabel(row: HiredRosterRow): string {
  if (row.kind === 'contractor') return 'Signed';
  const sep = row.offer.clauses.sepIraPercent;
  return sep != null && Number.isFinite(sep) ? `SEP IRA ${sep}%` : 'SEP IRA —';
}

const HiredContractorDetail: React.FC<{
  applicant: ContractorApplicant;
  job?: CrewJob | null;
  onOpenPacketDoc?: (doc: ContractorOnboardViewerOpen) => void;
}> = ({ applicant, job, onOpenPacketDoc }) => {
  const [agreement, setAgreement] = useState<ContractorAgreement | null>(null);
  useEffect(() => subscribeLatestContractorAgreement(applicant.id, setAgreement), [applicant.id]);
  const name = `${applicant.firstName} ${applicant.lastName}`.trim();
  return (
    <div className="people-hiring-detail">
      <div className="people-hiring-summary">
        <div className="people-hiring-summary-top">
          <h4>{name}</h4>
          <span className="people-hiring-status people-hiring-status--published">1099</span>
        </div>
        <p className="people-hiring-muted people-hiring-summary-meta">
          {applicant.lastJobTitle || 'Crew'}
          {applicant.email ? ` · ${applicant.email}` : ''}
          {applicant.phone ? ` · ${applicant.phone}` : ''}
          {agreement ? ` · ${formatContractorPay(agreement.fields)}` : ''}
        </p>
      </div>
      <ContractorAgreementPane
        applicant={applicant}
        agreement={agreement}
        job={job}
        onOpenPacketDoc={onOpenPacketDoc}
      />
    </div>
  );
};

const HiredRoster: React.FC<Props> = ({
  seat,
  rows,
  postings,
  crewJobs,
  selectedId,
  onSelectId,
  reviewerUid,
  onOpenPacketDoc,
}) => {
  const selected = rows.find(row => row.id === selectedId) || null;
  const noun = seat === 'employee' ? 'employees' : '1099s';
  return (
    <div className={`people-hiring-candidates-grid${selected ? ' is-split' : ''}`}>
      <section className="people-hub-panel people-hiring-apps">
        <div className="people-hiring-section-head">
          <h3>{seat === 'employee' ? 'Employees' : '1099s'}</h3>
          <span className="people-hiring-muted">{rows.length} {noun}</span>
        </div>
        {rows.length === 0 ? (
          <div className="people-hiring-empty">
            <p>
              {seat === 'employee'
                ? 'Nobody has finished an employment agreement yet.'
                : 'Nobody has finished a 1099 agreement yet.'}
            </p>
          </div>
        ) : (
          <div className="people-hiring-table-wrap">
            <table className="people-hiring-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>{seat === 'employee' ? 'Start' : 'Agreement'}</th>
                  <th>{seat === 'employee' ? 'Benefits' : 'Pay'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    className={selectedId === row.id ? 'people-hiring-row-selected' : ''}
                    onClick={() => onSelectId(row.id)}
                  >
                    <td><strong>{personName(row)}</strong></td>
                    <td>{roleLabel(row)}</td>
                    <td>
                      {row.kind === 'offer' ? startLabel(row.offer.fields.startDate) : 'Signed'}
                    </td>
                    <td>
                      {row.kind === 'contractor'
                        ? '—'
                        : seat === 'employee'
                          ? extraLabel(row)
                          : payLabel(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {selected?.kind === 'offer' && (
        <JobApplicationDetail
          key={selected.application.id}
          application={selected.application}
          posting={postings.find(p => p.id === selected.application.jobPostingId) || null}
          reviewerUid={reviewerUid}
          manageLabel="Manage"
        />
      )}
      {selected?.kind === 'contractor' && (
        <HiredContractorDetail
          applicant={selected.applicant}
          job={crewJobs.find(job => job.id === selected.applicant.lastJobId) || null}
          onOpenPacketDoc={onOpenPacketDoc}
        />
      )}
    </div>
  );
};

export default HiredRoster;
